"use client"

import { useCallback, useEffect, useState, useMemo, useRef } from "react"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline"
import CakeIcon from "@mui/icons-material/Cake"
import axios from "axios"
import useAuthStore from "../../../Zustand/Store/useAuthStore"
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore"
import NotAllowed from "../../../Exceptions/NotAllowed"
import toast from "react-hot-toast"
import PeopleIcon from "@mui/icons-material/People"
import { MAIN_URL } from "../../../Configurations/Urls"
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom"
import TableDataGeneric from "../../../Configurations/TableDataGeneric"
import Layout4 from "../../DataLayouts/Layout4"
import { operatorRequiresValue } from '../../../Components/filters/operators';
import { Box, Typography } from "@mui/material"
import InboxIcon from "@mui/icons-material/Inbox"

// Column fields must match backend response keys (department_name, designation_name, etc.)
const DEFAULT_COLUMNS = [
  {
    field: "employee_code",
    label: "Emp Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "name",
    label: "Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_name",
    label: "Department",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation_name",
    label: "Designation",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
]

// Backward compatibility: map legacy/saved column keys to canonical keys sent to API
const SORT_FIELD_ALIASES = {
  department: "department_name",
  designation: "designation_name",
  location: "location_name",
}

function EmployeeList({ mode }) {
  const { userData } = useAuthStore()
  const org = userData?.organization
  const { Permission } = usePermissionDataStore()
  const [showNotAllowed, setShowNotAllowed] = useState(false)

  const hasEditPermission = useMemo(() => {
    return (
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_EDIT")
    )
  }, [Permission])

  const hasDeletePermission = useMemo(() => {
    return (
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_DELETE")
    )
  }, [Permission])

  const [employee, setemployee] = useState([])
  const [loading, setLoading] = useState(true)
  const location = useLocation()
  const { id } = useParams()
  const fromDashboard = location.state?.fromDashboard || false
  const [searchParams, setSearchParams] = useSearchParams()

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  })

  const [tableConfig, setTableConfig] = useState(null)
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS)
  const [loadingConfig, setLoadingConfig] = useState(true)

  const fetchControllerRef = useRef(null)
  const hasInitializedURL = useRef(false)
 

  // Initialize URL parameters with defaults on mount
  useEffect(() => {
    if (hasInitializedURL.current) return

    const currentParams = new URLSearchParams(searchParams)
    let needsUpdate = false

    if (!currentParams.has("page")) {
      currentParams.set("page", "1")
      needsUpdate = true
    }

    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10")
      needsUpdate = true
    }

    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "employee_code")
      needsUpdate = true
    }

    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "desc")
      needsUpdate = true
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true })
    }

    hasInitializedURL.current = true
  }, [])

  // // Build query params from URL
  // const buildQueryParamsFromURL = useMemo(() => {
  //   const params = {}
  //   params.page = Number.parseInt(searchParams.get("page"), 10) || 1
  //   params.pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10

  //   const search = searchParams.get("search")
  //   if (search) params.search = search

  //   const sortBy = searchParams.get("sortBy")
  //   const sortOrder = searchParams.get("sortOrder")
  //   if (sortBy) {
  //     params.sortBy = sortBy
  //     params.sortOrder = sortOrder || "asc"
  //   }

  //   searchParams.forEach((value, key) => {
  //     if (key.startsWith("filter_") && value) {
  //       const filterKey = key.replace("filter_", "")
  //       params[filterKey] = value
  //     }
  //   })

  //   return params
  // }, [searchParams])

  // Fetch employees with filters - FIXED
  const fetchEmployeesWithFilters = useCallback(async () => {
    if (!org?.organization_id) return

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    fetchControllerRef.current = new AbortController()

    setLoading(true)
    try {
      // Build filters array from URL params
      const filters = []
      const processedFields = new Set() // Track processed fields to avoid duplicates
      let filterIndex = 0 // Track filter index
     
      searchParams.forEach((value, key) => {
        // Skip operator and logic params - they'll be processed with their field
        if (key.endsWith("_operator") || key.endsWith("_logic")) return
       
        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "")
         
          // Skip if already processed
          if (processedFields.has(field)) return
          processedFields.add(field)
         
          // Get operator from URL (e.g., filter_employee_code_operator=contains)
          const operatorKey = `filter_${field}_operator`
          const operator = searchParams.get(operatorKey) || 'contains' // Default to contains
         
          // Check if operator requires a value
          const requiresValue = operatorRequiresValue(operator)
         
          // Include filter if:
          // 1. Operator doesn't require value (today, this_week, this_month, is_null, is_not_null)
          // 2. OR value exists
          if (!requiresValue || value) {
            // For operators that don't require values, set value to null
            let filterValue = requiresValue ? value : null
           
            // Convert comma-separated string to array for operators that need arrays
            if (requiresValue && filterValue) {
              if (operator === 'equals' || operator === 'not_equals') {
                // Split by comma and trim whitespace, then convert to lowercase
                filterValue = filterValue.split(',').map(v => v.trim().toLowerCase())
              } else if (operator === 'between' || operator === 'not_between') {
                // Split by comma and trim whitespace for range operators
                filterValue = filterValue.split(',').map(v => v.trim())
              }
            }
           
            filters.push({
              field: field,
              operator: operator,
              value: filterValue
            })
           
            filterIndex++ // Increment index for next filter
          }
        }
      })

      // Global search: send as 'search' so backend metadata engine searches all filterable fields
      const search = searchParams.get("search")

      const requestBody = {
        filters: filters,
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        sort: {
          field: searchParams.get("sortBy") || "employee_code",
          direction: searchParams.get("sortOrder") || "desc"
        },
        ...(search && search.trim() !== "" && { search: search.trim() })
      }

      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org.organization_id}/employee/filters`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        },
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      // Debug logging
      console.log('Employee list API response:', result)
      console.log('Success:', result.success)
      console.log('Data length:', result.data?.length)
      console.log('Error:', result.error)
      console.log('Message:', result.message)

      if (result.success && result.data) {
        // Normalize profile_image_url to always be a full, usable URL for card view
        const normalizedEmployees = result.data.map((emp) => {
          const raw = emp?.profile_image_url

          if (!raw || typeof raw !== "string") {
            return { ...emp, profile_image_url: "" }
          }

          // If backend already sends full URL (http/https), keep as-is
          if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return { ...emp, profile_image_url: raw }
          }

          // If raw already includes its folder (employees/, persons/, interns/),
          // prepend the storage base only once
          if (
            raw.startsWith("employees/") ||
            raw.startsWith("persons/") ||
            raw.startsWith("interns/")
          ) {
            return {
              ...emp,
              profile_image_url: `${MAIN_URL}/storage/app/public/${raw}`,
            }
          }

          // Backward compat: treat as a plain filename in employees folder
          return {
            ...emp,
            profile_image_url: `${MAIN_URL}/storage/app/public/employees/${raw}`,
          }
        })

        setemployee(normalizedEmployees)

        if (result.pagination) {
          // Backend may return 'limit' instead of 'pageSize'
          const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage = Number.parseInt(searchParams.get("page"), 10) || 1;
         
          setPaginationData({
            page: result.pagination.page || requestedPage, // Use requested page if backend doesn't return it
            pageSize: pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          })
        }
      } else {
        console.warn('API returned unsuccessful response:', result)
        setemployee([])
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        })
        // Show error message to user
        if (result.message || result.error) {
          toast.error(result.message || result.error || 'Failed to fetch employees')
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Fetch aborted")
        return
      }
      console.error("Error fetching employees:", err)
      toast.error("Failed to fetch employees")
      setemployee([])
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      })
    } finally {
      setLoading(false)
    }
  }, [org?.organization_id, searchParams])

  // Load table configuration once
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false)
        return
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        })

        if (configRes.ok) {
          const configResponse = await configRes.json()
          const datagrids = configResponse.datagrids || []
          const orgKey = `Employees_grid_${org.organization_id}`
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey)

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration
            setTableConfig(serverCfg)

            if (serverCfg?.columns?.length > 0) {
              const processedColumns = serverCfg.columns
                .map((col) => {
                  const label = typeof col.label === "string" ? col.label.trim().toLowerCase() : ""
                  if (label === "name" || col.field === "Name") {
                    return { ...col, field: "name", label: "Name" }
                  }
                  // Normalize legacy keys to match backend response (for display and sort)
                  const field = col.field && String(col.field).toLowerCase()
                  if (field === "department") return { ...col, field: "department_name", label: col.label || "Department" }
                  if (field === "designation") return { ...col, field: "designation_name", label: col.label || "Designation" }
                  if (field === "location") return { ...col, field: "location_name", label: col.label || "Location" }
                  return col
                })
                .filter((col, idx, arr) => col.field !== "name" || arr.findIndex((c) => c.field === "name") === idx)
                .filter((col) => !["first_name", "middle_name", "last_name"].includes(col.field))

              const hasEmployeeName = processedColumns.some((c) => c.field === "name")
              const finalColumns = hasEmployeeName
                ? processedColumns
                : [
                    {
                      field: "name",
                      label: "Name",
                      visible: true,
                      width: 200,
                      filterable: true,
                      sortable: true,
                      pinned: "none",
                      required: false,
                    },
                    ...processedColumns,
                  ]

              setConfigColumns(finalColumns)
            } else {
              setConfigColumns(DEFAULT_COLUMNS)
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS)
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error)
        setConfigColumns(DEFAULT_COLUMNS)
      } finally {
        setLoadingConfig(false)
      }
    }

    loadTableConfiguration()
  }, [org?.organization_id])

  // Fetch employees when URL params change
  useEffect(() => {
    if (org?.organization_id && !loadingConfig && hasInitializedURL.current) {
      fetchEmployeesWithFilters()
    }

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort()
      }
    }
  }, [org?.organization_id, loadingConfig, searchParams, fetchEmployeesWithFilters])

  const deleteemployee = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id
        await axios.delete(`${MAIN_URL}/api/organizations/${org_id}/employee/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
      } catch (error) {
        if (error.response && error.response.status === 401) {
          window.location.href = "/login"
          toast.error("Session Expired!")
        }
      }
    },
    [org],
  )

  const navigate = useNavigate()

  const onclickRow = useCallback(
    (item) => {
      // Ensure we have the correct organization_person_id
      const orgPersonId = item?.organization_person_id || item?.id
      if (!orgPersonId) {
        console.error('Missing organization_person_id in row data:', item)
        toast.error('Unable to open employee profile: Missing employee ID')
        return
      }
      const params = new URLSearchParams(searchParams)
      navigate(`/organization/employee/employee-details/employee-profile?id=${orgPersonId}&${params.toString()}`)
    },
    [navigate, searchParams],
  )

  // Handle sort change - update URL parameters; map legacy column keys for backward compatibility
  const handleSortChange = useCallback(
    (sortField, sortDirection) => {
      const raw = (sortField && String(sortField).trim()) || ""
      const normalizedField = raw.toLowerCase()
      const canonicalField = SORT_FIELD_ALIASES[normalizedField] ?? (normalizedField || raw)

      const params = new URLSearchParams(searchParams)
      params.set("sortBy", canonicalField)
      params.set("sortOrder", sortDirection)
      params.set("page", "1") // Reset to first page on sort
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleDelete = useCallback(
    async (id) => {
      if (!hasDeletePermission) {
        setShowNotAllowed(true)
        return Promise.reject(new Error("Not allowed"))
      }
      try {
        const response = await fetch(`${MAIN_URL}/api/organizations/${org?.organization_id}/employee/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        toast.success("Employee deleted successfully")

        return Promise.resolve()
      } catch (error) {
        toast.error("Failed to delete employee")
        return Promise.reject(error)
      }
    },
    [org?.organization_id, hasDeletePermission],
  )

  const handleEdit = useCallback(
    (item) => {
      const employeeId = item?.organization_person_id || item?.employee_id || item?.id;
      if (!employeeId) {
        console.error('Employee ID is missing:', item);
        toast.error('Unable to edit: Employee ID is missing');
        return;
      }
      navigate(`/organization/employee/employee-details/edit/${employeeId}`)
    },
    [navigate],
  )

  const carddata = useMemo(
    () => [
      { key: "profile_image_url", type: "photo" },
      { key: "name", type: "String" },
      { key: "designation_name", type: "String" },
    ],
    [],
  )

  const icons = useMemo(
    () => [
      {
        key: "exitToAppIcon",
        element: <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
      },
      {
        key: "viewHeadlineIcon",
        element: <ViewHeadlineIcon color="primary" />,
      },
      {
        key: "cakeIcon",
        element: <CakeIcon sx={{ color: "red" }} />,
      },
      {
        key: "peopleIcon",
        element: <PeopleIcon sx={{ color: "primary" }} />,
      },
    ],
    [],
  )

  const messages = useMemo(
    () => [
      "Employee details",
      "Employee details",
      "Add Employee",
      "Employee, Deleting This Employee will delete its Entire data from Employees Record of this Organization Permanently",
    ],
    [],
  )


       const handleShow = useCallback(
    (item) => {
      navigate(`/organization/employee/employee-details/view/${item.organization_person_id}`)
    },
    [navigate],
  )


  if (showNotAllowed) {
    return <NotAllowed />
  }

  return (
    <>
      <Layout4
        loading={loading}
        add_action={"EMPLOYEE_ADD"}
        delete_action={"EMPLOYEE_DELETE"}
        heading={
          <div style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
            Employees
          </div>
        }
        btnName={"Add Employee"}
        onAddBtClick={() => null}
        onEditBtClick={() => null}
        Data={employee}
        Icons={icons}
        messages={messages}
        Route={"/organization/employee/employee-details"}
        setData={setemployee}
        DeleteFunc={deleteemployee}
      />
      {!loadingConfig && (
        <>
          {!loading && employee.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                py: 8,
                px: 3,
              }}
            >
              <InboxIcon
                sx={{
                  fontSize: 80,
                  color: "grey.400",
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                No Employees Found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  maxWidth: "400px",
                }}
              >
                There are currently no employees in the system. Add a new employee to get started.
              </Typography>
            </Box>
          ) : (
            <>
              {fromDashboard ? (
                <TableDataGeneric
                  tableName="Employees"
                  primaryKey="organization_person_id"
                  heading="Employees"
                  data={employee}
                  sortname={"name"}
                  showActions={false}
                  CardData={carddata}
                  Route="/organization/employee/employee-details"
                  DeleteFunc={hasDeletePermission ? handleDelete : undefined}
                  EditFunc={hasEditPermission ? handleEdit : undefined}
                  handleShow={handleShow}
                  token={localStorage.getItem("token")}
                  configss={configColumns}
                  onclickRow={onclickRow}
                  linkType={"Name"}
                  paginationData={paginationData}
                  onSortChange={handleSortChange}
                  edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
                  {...(tableConfig && { config: tableConfig })}
                />
              ) : (
                <TableDataGeneric
                  tableName="Employees"
                  primaryKey={"organization_person_id"}
                  recordKey={"employee_code"}
                  recordLabel={"Employee Code"}
                  heading="Employees"
                  data={employee}
                  sortname={"name"}
                  showActions={true}
                  CardData={carddata}
                  Route="/organization/employee/employee-details"
                  DeleteFunc={hasDeletePermission ? handleDelete : undefined}
                  EditFunc={hasEditPermission ? handleEdit : undefined}
                  handleShow={handleShow}
                  onclickRow={onclickRow}
                  token={localStorage.getItem("token")}
                  linkType={"name"}
                  configss={configColumns}
                  paginationData={paginationData}
                  onSortChange={handleSortChange}
                  edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
                  {...(tableConfig && { config: tableConfig })}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export default EmployeeList
